import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  CalendarDays,
  Check,
  FileText,
  Info,
  LockKeyhole,
  Map,
  UserRound,
  Wrench,
} from 'lucide-react';
import { ProviderLayout } from '../../components/layouts';
import { useAuth } from '../../context/AuthContext';
import { authApi } from '../../api/authApi';
import { userApi } from '../../api/userApi';
import { servicesApi } from '../../api/servicesApi';
import { verificationApi } from '../../api/verificationApi';
import ServiceIcon from '../../components/ServiceIcon';
import VerificationDocumentUpload from '../../components/VerificationDocumentUpload';
import AvailabilityScheduler from '../../components/AvailabilityScheduler';
import AvatarUpload from '../../components/AvatarUpload';
import ServiceAreaMap from '../../components/ServiceAreaMap';
import ChangePassword from '../../components/ChangePassword';
import AustralianPhoneInput from '../../components/AustralianPhoneInput';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  Input,
  Select,
  Tabs,
  TabButton,
  Textarea,
  Toast,
  cn,
} from '../../components/ui';

const DOC_DEFS = [
  { type: 'id', title: 'Government ID', description: 'Driver licence, passport or Australian proof-of-age card. Must show your full name, photo and date of birth.' },
  { type: 'insurance', title: 'Public Liability Insurance', description: 'A current Certificate of Currency showing $5M+ cover (or trade-equivalent).' },
];
const STATES = ['QLD', 'NSW', 'VIC', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
const STATE_LABELS = { QLD: 'Queensland', NSW: 'New South Wales', VIC: 'Victoria', SA: 'South Australia', WA: 'Western Australia', TAS: 'Tasmania', NT: 'Northern Territory', ACT: 'ACT' };

const fmtDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

const bandLabel = (service) => {
  const min = Number(service.hourlyRateMin) || 0;
  const max = Number(service.hourlyRateMax) || 0;
  if (!min && !max) return 'No Giggs rate limit set';
  if (min && max) return `Giggs allowed band: $${min}–$${max}/hr`;
  if (min) return `Giggs minimum: $${min}/hr`;
  return `Giggs maximum: $${max}/hr`;
};

const InfoNote = ({ children }) => (
  <div className="flex items-start gap-2 rounded-xl bg-emerald-50/70 px-4 py-3 font-dm text-[13px] font-normal leading-5 text-giggs-text ring-1 ring-inset ring-emerald-100">
    <Info size={16} className="mt-0.5 shrink-0 text-giggs-green" /><div>{children}</div>
  </div>
);

const SettingsForm = ({ title, onSubmit, children, actions }) => (
  <Card><form onSubmit={onSubmit}><CardHeader title={title} /><CardBody className="flex flex-col gap-5">{children}{actions && <div className="flex justify-end border-t border-slate-100 pt-5">{actions}</div>}</CardBody></form></Card>
);

// Preserve links and notifications that still target the former Account tab.
const ProviderSettings = () => {
  const location = useLocation();
  const tab = new URLSearchParams(location.search).get('tab');
  if (location.state?.tab === 'account' || tab === 'account' || location.hash === '#account') {
    return <Navigate to="/provider/account" replace />;
  }
  return <ProviderSettingsForm />;
};

const ProviderSettingsForm = () => {
  const { user, updateUser } = useAuth();
  const routeLocation = useLocation();
  const requestedTab = routeLocation.state?.tab;
  const [activeTab, setActiveTab] = useState(requestedTab === 'location' ? 'areas' : (requestedTab || 'profile'));
  const [savingTab, setSavingTab] = useState(null);
  const [savedTab, setSavedTab] = useState(null);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState({ name: '', email: '', phone: '', bio: '', experience: '' });
  const [location, setLocation] = useState({ address: '', suburb: '', state: 'QLD', postcode: '' });
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  const [serviceRates, setServiceRates] = useState({});
  const [availableServices, setAvailableServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [serviceArea, setServiceArea] = useState({ address: '', lat: null, lng: null, radiusKm: 10, additionalAreas: [] });
  const [newArea, setNewArea] = useState({ suburb: '', state: 'QLD', postcode: '' });
  const [availability, setAvailability] = useState({ timezone: 'Australia/Brisbane', weekly: [], exceptions: [] });
  const [verification, setVerification] = useState({ verificationStatus: 'pending', verificationDocuments: { id: {}, insurance: {} }, additionalVerificationRequest: { active: false, message: '', requestedAt: null } });
  const [verificationLoading, setVerificationLoading] = useState(true);

  // Registration initially caches the user returned by /register. Refresh here so
  // location, service area, selected services and any optional onboarding data
  // saved immediately after registration are visible without a logout/reload.
  useEffect(() => {
    let cancelled = false;
    authApi.checkAuth()
      .then((res) => {
        if (!cancelled && res?.success && res?.data) updateUser(res.data);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [updateUser]);

  useEffect(() => {
    if (!user) return;
    setProfile({ name: user.name || '', email: user.email || '', phone: user.phone || '', bio: user.bio || '', experience: user.experience || '' });
    setLocation({ address: user.location?.address || '', suburb: user.location?.suburb || '', state: user.location?.state || 'QLD', postcode: user.location?.postcode || '' });

    const ids = [];
    const rates = {};
    (user.services || []).forEach((entry) => {
      const serviceId = entry && typeof entry.service === 'object' && entry.service ? entry.service._id : entry?.service;
      if (!serviceId) return;
      ids.push(serviceId);
      rates[serviceId] = entry.hourlyRateMin ?? entry.hourlyRateMax ?? '';
    });
    setSelectedServiceIds(ids);
    setServiceRates(rates);

    const area = user.serviceArea || {};
    const coords = area.geo?.coordinates || [];
    setServiceArea({
      address: area.address || user.location?.address || '',
      lng: Number.isFinite(coords[0]) ? coords[0] : null,
      lat: Number.isFinite(coords[1]) ? coords[1] : null,
      radiusKm: area.radiusKm || 10,
      additionalAreas: Array.isArray(area.additionalAreas)
        ? area.additionalAreas.map((item) => ({ suburb: item.suburb || '', state: item.state || '', postcode: item.postcode || '' }))
        : [],
    });

    const currentAvailability = user.availability || {};
    setAvailability({
      timezone: currentAvailability.timezone || 'Australia/Brisbane',
      weekly: Array.isArray(currentAvailability.weekly) ? currentAvailability.weekly.map((item) => ({ day: item.day, enabled: item.enabled !== false, slots: Array.isArray(item.slots) ? item.slots.map((slot) => ({ start: slot.start, end: slot.end })) : [] })) : [],
      exceptions: Array.isArray(currentAvailability.exceptions) ? currentAvailability.exceptions.map((item) => ({ date: item.date ? new Date(item.date).toISOString().slice(0, 10) : '', available: Boolean(item.available), slots: Array.isArray(item.slots) ? item.slots.map((slot) => ({ start: slot.start, end: slot.end })) : [] })).filter((item) => item.date) : [],
    });
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    servicesApi.list().then((data) => { if (!cancelled) setAvailableServices(Array.isArray(data) ? data : []); }).catch(() => { if (!cancelled) setError('Failed to load services.'); }).finally(() => { if (!cancelled) setServicesLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    verificationApi.getMyVerification().then((res) => {
      if (cancelled || !res?.data) return;
      setVerification({ verificationStatus: res.data.verificationStatus || 'pending', verificationDocuments: res.data.verificationDocuments || { id: {}, insurance: {} }, additionalVerificationRequest: res.data.additionalVerificationRequest || { active: false, message: '', requestedAt: null } });
    }).catch(() => {}).finally(() => { if (!cancelled) setVerificationLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const docs = verification.verificationDocuments || {};
  const uploadedCount = DOC_DEFS.filter(({ type }) => Boolean(docs[type]?.url)).length;
  const documentActionCount = DOC_DEFS.filter(({ type }) => !docs[type]?.url || docs[type]?.status === 'rejected').length + (verification.additionalVerificationRequest?.active ? 1 : 0);
  const verificationMeta = useMemo(() => {
    if (verification.verificationStatus === 'approved') return { label: 'Verified', variant: 'success', desc: 'Your account is fully verified. You can apply to Gigs without restriction.' };
    if (verification.verificationStatus === 'rejected') return { label: 'Action required', variant: 'danger', desc: 'One or more documents need attention. Review the reason and upload the corrected document.' };
    if (uploadedCount < 2) return { label: 'Review pending', variant: 'warning', desc: `${uploadedCount} of 2 documents uploaded. Upload both required documents so Giggs can review your account.` };
    return { label: 'Review pending', variant: 'warning', desc: 'Both required documents are uploaded. Your account is waiting for Giggs review.' };
  }, [verification.verificationStatus, uploadedCount]);

  const flashSaved = (tab) => { setSavedTab(tab); setTimeout(() => setSavedTab(null), 2500); };

  const saveProfile = async (event) => {
    event.preventDefault(); setError(''); setSavingTab('profile');
    try {
      const res = await userApi.updateProfile({ name: profile.name, phone: profile.phone, bio: profile.bio });
      await userApi.completeOnboarding({ experience: profile.experience, emergencyAvailable: Boolean(user?.availability?.emergencyAvailable) }).catch(() => {});
      updateUser({ ...res.data, experience: profile.experience }); flashSaved('profile');
    } catch (err) { setError(err.message || 'Failed to update profile.'); } finally { setSavingTab(null); }
  };

  const saveServices = async (event) => {
    event.preventDefault(); setError('');
    const payload = [];
    for (const id of selectedServiceIds) {
      const service = availableServices.find((item) => item._id === id);
      const name = service?.name || 'this service';
      const actualRate = Number(serviceRates[id]);
      if (serviceRates[id] === '' || !Number.isFinite(actualRate) || actualRate < 0) return setError(`Enter a valid actual hourly rate for “${name}”.`);
      const bandMin = Number(service?.hourlyRateMin) || 0;
      const bandMax = Number(service?.hourlyRateMax) || 0;
      if (bandMin > 0 && actualRate < bandMin) return setError(`For “${name}”, your rate can't go below $${bandMin}/hr.`);
      if (bandMax > 0 && actualRate > bandMax) return setError(`For “${name}”, your rate can't go above $${bandMax}/hr.`);
      payload.push({ service: id, hourlyRateMin: actualRate, hourlyRateMax: actualRate });
    }
    setSavingTab('services');
    try { const res = await userApi.updateServices({ services: payload }); updateUser(res.data); flashSaved('services'); } catch (err) { setError(err.message || 'Failed to update services.'); } finally { setSavingTab(null); }
  };

  const toggleService = (service) => {
    const id = service._id;
    if (selectedServiceIds.includes(id)) return setSelectedServiceIds((current) => current.filter((item) => item !== id));
    setSelectedServiceIds((current) => [...current, id]);
    setServiceRates((current) => current[id] !== undefined ? current : { ...current, [id]: service.hourlyRateMin || '' });
  };

  const addArea = () => {
    const postcode = (newArea.postcode || '').trim();
    if (!postcode) return setError('Enter a postcode to add a service area.');
    setError('');
    setServiceArea((current) => ({ ...current, additionalAreas: [...current.additionalAreas, { suburb: newArea.suburb.trim(), state: newArea.state, postcode }] }));
    setNewArea({ suburb: '', state: 'QLD', postcode: '' });
  };
  const removeArea = (index) => setServiceArea((current) => ({ ...current, additionalAreas: current.additionalAreas.filter((_, itemIndex) => itemIndex !== index) }));

  const saveServiceArea = async (event) => {
    event.preventDefault();
    setError('');
    setSavingTab('areas');
    try {
      if (!location.address.trim()) return setError('Enter your base address.');
      if (!location.suburb.trim()) return setError('Enter your suburb.');
      if (!location.state) return setError('Select your state.');
      if (!/^\d{4}$/.test(location.postcode.trim())) return setError('Enter a valid 4-digit postcode.');

      await userApi.updateProfile({
        location: {
          address: location.address.trim(),
          suburb: location.suburb.trim(),
          state: location.state,
          postcode: location.postcode.trim(),
        },
      });

      const areaPayload = {
        address: serviceArea.address || location.address.trim(),
        radiusKm: serviceArea.radiusKm,
        additionalAreas: serviceArea.additionalAreas,
      };
      if (Number.isFinite(serviceArea.lat) && Number.isFinite(serviceArea.lng)) {
        areaPayload.lat = serviceArea.lat;
        areaPayload.lng = serviceArea.lng;
      }

      const res = await userApi.updateServiceArea(areaPayload);
      updateUser(res.data);
      flashSaved('areas');
    } catch (err) {
      setError(err.message || 'Failed to update service area.');
    } finally {
      setSavingTab(null);
    }
  };

  const handleDocChange = (payload) => {
    if (!payload) return;
    setVerification((current) => ({ verificationStatus: payload.verificationStatus || 'pending', verificationDocuments: payload.verificationDocuments || { id: {}, insurance: {} }, additionalVerificationRequest: payload.additionalVerificationRequest || current.additionalVerificationRequest }));
    if (user) updateUser({ ...user, verificationStatus: payload.verificationStatus, verificationDocuments: payload.verificationDocuments });
    flashSaved('documents');
  };

  const saveAvailability = async (event) => {
    event.preventDefault(); setError('');
    const invalidSlot = (slots) => (slots || []).some((slot) => !slot.start || !slot.end || slot.start >= slot.end);
    for (const item of availability.weekly) if (item.enabled && invalidSlot(item.slots)) return setError(`Check your ${item.day} hours — start must be before end.`);
    for (const exception of availability.exceptions) if (exception.available && invalidSlot(exception.slots)) return setError(`Check the custom hours on ${exception.date}.`);
    setSavingTab('availability');
    try { const res = await userApi.updateAvailability({ timezone: availability.timezone, weekly: availability.weekly.map((item) => ({ day: item.day, enabled: item.enabled, slots: item.enabled ? item.slots : [] })), exceptions: availability.exceptions.map((item) => ({ date: item.date, available: item.available, slots: item.available ? item.slots : [] })) }); updateUser(res.data); flashSaved('availability'); } catch (err) { setError(err.message || 'Failed to update availability.'); } finally { setSavingTab(null); }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', Icon: UserRound },
    { id: 'services', label: 'Services', Icon: Wrench },
    { id: 'areas', label: 'Service Areas', Icon: Map },
    { id: 'availability', label: 'Availability', Icon: CalendarDays },
    { id: 'documents', label: 'Documents', Icon: FileText, count: documentActionCount || undefined },
    { id: 'security', label: 'Security', Icon: LockKeyhole },
  ];

  const providerSubtitle = `Provider${user?.location?.suburb ? ` · ${user.location.suburb}` : ''}${user?.location?.state ? `, ${user.location.state}` : ''}${user?.location?.postcode ? ` ${user.location.postcode}` : ''}`;

  return (
    <ProviderLayout title="Settings">
      <div className="font-dm">
        {savedTab && <Toast><Check size={16} /> Changes saved successfully</Toast>}
        {error && <Alert variant="danger" className="mb-4 text-center">{error}</Alert>}
        <div className="mb-5 overflow-x-auto rounded-2xl bg-white/80 p-2"><Tabs className="min-w-max sm:min-w-0">{tabs.map(({ id, label, Icon, count }) => <TabButton key={id} active={activeTab === id} count={count} onClick={() => { setActiveTab(id); setError(''); }}><Icon size={16} /> {label}</TabButton>)}</Tabs></div>

        {activeTab === 'profile' && <SettingsForm title="Provider Profile" onSubmit={saveProfile} actions={<Button type="submit" disabled={savingTab === 'profile'}>{savingTab === 'profile' ? 'Saving…' : 'Save Changes'}</Button>}>
          <AvatarUpload gradient="linear-gradient(135deg, #C7E8D4, #AAF0C4)" subtitle={providerSubtitle} />
          <Field label="Full Name"><Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} /></Field>
          <div className="grid gap-4 md:grid-cols-2"><Field label="Email"><Input type="email" value={profile.email} disabled /></Field><Field label="Phone"><AustralianPhoneInput value={profile.phone} onChange={(phone) => setProfile({ ...profile, phone })} /></Field></div>
          <Field label="Experience"><Textarea rows={3} value={profile.experience} onChange={(e) => setProfile({ ...profile, experience: e.target.value })} placeholder="Briefly describe your relevant experience" /></Field>
          <Field label="Bio / About You"><Textarea rows={4} value={profile.bio} onChange={(e) => setProfile({ ...profile, bio: e.target.value })} /></Field>
        </SettingsForm>}

        {activeTab === 'services' && <SettingsForm title="Services & Rates" onSubmit={saveServices} actions={<Button type="submit" disabled={savingTab === 'services' || servicesLoading}>{savingTab === 'services' ? 'Saving…' : 'Save Services'}</Button>}>
          <InfoNote>Select the services you actually offer. Only selected services show a rate field, and each service uses one <strong className="font-semibold">Actual Rate</strong>.</InfoNote>
          {servicesLoading ? <div className="py-5 text-center text-sm text-giggs-muted">Loading services…</div> : <div className="flex flex-col gap-3">{availableServices.filter((service) => service.status === 'Active').map((service) => {
            const selected = selectedServiceIds.includes(service._id);
            return <div key={service._id} className={cn('overflow-hidden rounded-xl bg-white ring-1 transition', selected ? 'ring-giggs-green' : 'ring-slate-200')}>
              <button type="button" onClick={() => toggleService(service)} className={cn('flex w-full items-center gap-3 border-0 px-4 py-3.5 text-left', selected ? 'bg-giggs-green/[0.05]' : 'bg-white hover:bg-slate-50')}><span className={cn('flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md ring-2 ring-inset', selected ? 'bg-giggs-green text-white ring-giggs-green' : 'bg-white ring-slate-300')}>{selected && <Check size={14} strokeWidth={3} />}</span><ServiceIcon serviceId={service.icon || service.slug} size={18} /><span className="min-w-0 flex-1"><span className="block text-sm font-semibold text-giggs-ink">{service.name}</span><span className="mt-0.5 block text-xs font-normal text-giggs-muted">{bandLabel(service)}</span></span></button>
              {selected && <div className="border-t border-slate-100 px-4 py-4"><Field label="Your Actual Rate ($/hr)" hint="This is the hourly rate clients will see for this service."><Input type="number" min="0" step="1" value={serviceRates[service._id] ?? ''} onChange={(e) => setServiceRates((current) => ({ ...current, [service._id]: e.target.value }))} placeholder={service.hourlyRateMin ? String(service.hourlyRateMin) : '0'} /></Field></div>}
            </div>;
          })}</div>}
        </SettingsForm>}

        {activeTab === 'areas' && <SettingsForm title="Service Areas" onSubmit={saveServiceArea} actions={<Button type="submit" disabled={savingTab === 'areas'}>{savingTab === 'areas' ? 'Saving…' : 'Save Service Areas'}</Button>}>
          <InfoNote>Your registration location and coverage radius are loaded here automatically. You can update them any time.</InfoNote>
          <ServiceAreaMap
            apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
            center={serviceArea.lat != null && serviceArea.lng != null ? { lat: serviceArea.lat, lng: serviceArea.lng } : null}
            radiusKm={serviceArea.radiusKm}
            initialAddress={serviceArea.address || location.address}
            onPlaceSelected={({ address, lat, lng }) => {
              setServiceArea((current) => ({ ...current, address, lat, lng }));
              setLocation((current) => ({ ...current, address }));
            }}
          />
          <div className="grid gap-4 md:grid-cols-3">
            <Field label="Suburb"><Input value={location.suburb} onChange={(e) => setLocation({ ...location, suburb: e.target.value })} /></Field>
            <Field label="State"><Select value={location.state} onChange={(e) => setLocation({ ...location, state: e.target.value })}>{STATES.map((state) => <option key={state} value={state}>{STATE_LABELS[state]}</option>)}</Select></Field>
            <Field label="Postcode"><Input maxLength="4" inputMode="numeric" value={location.postcode} onChange={(e) => setLocation({ ...location, postcode: e.target.value.replace(/\D/g, '').slice(0, 4) })} /></Field>
          </div>
          <Field label={`Coverage radius: ${serviceArea.radiusKm} km`}><div className="flex items-center gap-3"><input className="h-2 min-w-0 flex-1 cursor-pointer accent-giggs-green" type="range" min="1" max="100" value={serviceArea.radiusKm || 1} onChange={(e) => setServiceArea((current) => ({ ...current, radiusKm: Number(e.target.value) }))} /><Input className="w-[82px] shrink-0" type="number" min="1" max="100" value={serviceArea.radiusKm} onChange={(e) => setServiceArea((current) => ({ ...current, radiusKm: Number(e.target.value) }))} /></div></Field>
          <div className="border-t border-slate-100 pt-5"><h3 className="m-0 font-nunito text-[15px] font-bold text-giggs-ink">Extra service areas</h3><p className="mb-4 mt-1 text-[13px] font-normal text-giggs-muted">Add specific suburbs/postcodes beyond your radius.</p><div className="grid items-end gap-3 sm:grid-cols-[2fr_1fr_1fr_auto]"><Field label="Suburb"><Input value={newArea.suburb} onChange={(e) => setNewArea({ ...newArea, suburb: e.target.value })} /></Field><Field label="State"><Select value={newArea.state} onChange={(e) => setNewArea({ ...newArea, state: e.target.value })}>{STATES.map((state) => <option key={state}>{state}</option>)}</Select></Field><Field label="Postcode"><Input maxLength="4" value={newArea.postcode} onChange={(e) => setNewArea({ ...newArea, postcode: e.target.value })} /></Field><Button type="button" onClick={addArea}>Add</Button></div>{serviceArea.additionalAreas.length > 0 && <div className="mt-4 flex flex-wrap gap-2">{serviceArea.additionalAreas.map((area, index) => <span key={`${area.postcode}-${index}`} className="inline-flex items-center gap-2 rounded-full bg-giggs-green/[0.08] px-3 py-1.5 text-[13px] text-[#177552]">{[area.suburb, area.state, area.postcode].filter(Boolean).join(' ')}<button type="button" onClick={() => removeArea(index)} className="border-0 bg-transparent p-0 text-base leading-none text-[#177552] hover:text-red-600">×</button></span>)}</div>}</div>
        </SettingsForm>}

        {activeTab === 'availability' && <SettingsForm title="Availability" onSubmit={saveAvailability} actions={<Button type="submit" disabled={savingTab === 'availability'}>{savingTab === 'availability' ? 'Saving…' : 'Save Availability'}</Button>}><InfoNote>Set weekly working hours and date overrides. The current scheduler also lets you copy a day and adjust individual times.</InfoNote><AvailabilityScheduler value={availability} onChange={setAvailability} /></SettingsForm>}

        {activeTab === 'documents' && <Card><CardHeader title="Verification Documents" /><CardBody className="flex flex-col gap-4">{verificationLoading ? <div className="py-5 text-center text-sm text-giggs-muted">Loading verification status…</div> : <><div className="flex flex-col gap-3 rounded-xl bg-slate-50 p-4 ring-1 ring-inset ring-slate-100 sm:flex-row sm:items-center sm:justify-between"><div><h3 className="m-0 font-nunito text-base font-bold text-giggs-ink">Account Verification</h3><p className="mb-0 mt-1 text-[13px] font-normal leading-5 text-giggs-muted">{verificationMeta.desc}</p></div><Badge variant={verificationMeta.variant}>{verificationMeta.label}</Badge></div>{verification.additionalVerificationRequest?.active && <Alert variant="warning" title="Admin requested additional verification."><span className="whitespace-pre-wrap">{verification.additionalVerificationRequest.message}</span>{verification.additionalVerificationRequest.requestedAt && <span className="mt-1 block text-[11px] opacity-75">Requested {fmtDate(verification.additionalVerificationRequest.requestedAt)}</span>}</Alert>}<Alert variant="info" title="Your documents are private.">Only you and Giggs admins can view them. They are used for verification and dispute resolution.</Alert><div className="flex flex-col gap-3">{DOC_DEFS.map((definition) => <VerificationDocumentUpload key={definition.type} type={definition.type} title={definition.title} description={definition.description} doc={docs[definition.type] || {}} onChange={handleDocChange} />)}</div></>}</CardBody></Card>}

        {activeTab === 'security' && <ChangePassword />}
      </div>
    </ProviderLayout>
  );
};

export default ProviderSettings;
